import styled from '@emotion/styled';
import TextareaAutosize from '@mui/material/TextareaAutosize';
import type { Theme } from '@trinity/chat-components/types/theme';
import { FormTitle } from './DynamicForm.styles.base';
import { getThemeValue, getThemeColor, THEME } from './DynamicForm.styles.theme';

type StyledComponentProps = {
  theme?: Theme.Theme;
};

// =============================================================================
// FORM LAYOUT COMPONENTS
// =============================================================================

export const FormGrid = styled.div<StyledComponentProps>(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
  gap: THEME.spacing.lg,
  marginBottom: 0,
  '@media (max-width: 600px)': {
    gridTemplateColumns: '1fr',
  },
}));

export const FormColumn = styled.div<StyledComponentProps>(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: THEME.spacing.xs,
}));

export const FormRowFullWidth = styled.div<StyledComponentProps>(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: THEME.spacing.xs,
  marginBottom: THEME.spacing.lg,
  gridColumn: '1 / -1', // Span all columns
  width: '100%',
  maxWidth: '100%',
  minWidth: '100%',
}));

export const SectionContainer = styled.div<StyledComponentProps>(({ theme }) => ({
  gridColumn: '1 / -1', // Span all columns
  marginBottom: THEME.spacing.md,
}));

export const SectionTitle = styled.h3<StyledComponentProps>(({ theme }) => ({
  fontSize: THEME.fontSize.lg,
  fontWeight: 600,
  color: getThemeColor.text(theme),
  margin: 0,
  marginBottom: THEME.spacing.sm,
}));

export const SectionDivider = styled.hr<StyledComponentProps>(({ theme }) => ({
  border: 'none',
  borderTop: `1px solid ${getThemeColor.border(theme)}`,
  margin: `${THEME.spacing.xs} 0 ${THEME.spacing.md} 0`,
}));

// =============================================================================
// FIELD COMPONENTS
// =============================================================================

export const FieldLabel = styled.label<StyledComponentProps>(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: THEME.spacing.xs,
  fontSize: THEME.fontSize.sm,
  color: getThemeColor.text(theme),
  marginBottom: THEME.spacing.xs,
  fontWeight: 600, // Semi-bold

  // Style for required asterisk
  '& .required-asterisk': {
    color: getThemeColor.error(theme),
    fontWeight: 'normal',
  },
}));

export const Textarea = styled(TextareaAutosize)<StyledComponentProps>(({ theme }) => ({
  width: '100%',
  boxSizing: 'border-box',
  borderStyle: 'solid',
  borderWidth: '1px',
  borderRadius: THEME.borderRadius.md,
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  outline: 'none',
  resize: 'vertical',
  minHeight: '20px',

  lineHeight: 1.5,
  fontFamily: getThemeValue(
    (t) => t.fonts?.regular,
    'inherit',
    theme,
  ),
  fontSize: THEME.fontSize.sm,
  appearance: 'none',
  transition: 'border-color 0.2s, box-shadow 0.2s',

  borderColor: getThemeColor.border(theme),
  backgroundColor: getThemeColor.background(theme),
  color: getThemeColor.text(theme),

  '&:focus': {
    borderColor: getThemeColor.primary(theme),
    borderWidth: '2px',
  },

  '&:disabled': {
    backgroundColor: getThemeValue(
      (t) => t.palette?.gray?.light3,
      getThemeColor.backgroundLight(theme),
      theme,
    ),
    color: getThemeColor.textSecondary(theme),
    cursor: 'not-allowed',
  },

  '&::placeholder': {
    color: getThemeColor.textSecondary(theme),
    opacity: 1,
  },
}));

export const Input = styled.input<StyledComponentProps>(({ theme }) => ({
  width: '100%',
  height: '38px',
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  border: `1px solid ${getThemeColor.border(theme)}`,
  borderRadius: THEME.borderRadius.md,
  fontSize: THEME.fontSize.sm,
  fontFamily: getThemeValue(
    (t) => t.fonts?.regular,
    'inherit',
    theme,
  ),
  transition: 'border-color 0.2s',
  backgroundColor: getThemeColor.background(theme),
  color: getThemeColor.text(theme),
  boxSizing: 'border-box',
  appearance: 'none',

  '&:focus': {
    outline: 'none',
    borderColor: getThemeColor.primary(theme),
    borderWidth: '2px',
  },

  '&:read-only': {
    backgroundColor: getThemeValue(
      (t) => t.palette?.gray?.light3,
      getThemeColor.backgroundLight(theme),
      theme,
    ),
    color: getThemeColor.textSecondary(theme),
    cursor: 'not-allowed',
  },

  '&::placeholder': {
    color: getThemeColor.textSecondary(theme),
    opacity: 1,
  },
}));

// =============================================================================
// STATUS AND DISPLAY COMPONENTS
// =============================================================================

export const ErrorMessage = styled.div<StyledComponentProps>(({ theme }) => ({
  backgroundColor: getThemeValue(
    (t) => t.palette?.red?.secondary,
    '#f8d7da',
    theme,
  ),
  color: getThemeColor.error(theme),
  border: `1px solid #f5c6cb`,
  borderRadius: THEME.borderRadius.md,
  padding: THEME.spacing.lg,
  margin: `${THEME.spacing.lg} 0`,
  fontSize: THEME.fontSize.base,
  textAlign: 'center',
  '&[role="alert"]': {
    fontWeight: 500,
  },
}));

export const CompletionTitle = styled(FormTitle)({
  marginBottom: THEME.spacing.lg,
});

export const RichTextSummary = styled.div<StyledComponentProps>(({ theme }) => ({
  whiteSpace: 'pre-wrap',
  backgroundColor: getThemeValue(
    (t) => t.palette?.gray?.light3,
    getThemeColor.backgroundLight(theme),
    theme,
  ),
  padding: THEME.spacing.lg,
  borderRadius: THEME.borderRadius.md,
  border: `1px solid ${getThemeColor.border(theme)}`,
  color: getThemeColor.text(theme),
  fontSize: THEME.fontSize.base,
  lineHeight: 1.7,
}));

export const CompletionMetadata = styled.div<StyledComponentProps>(({ theme }) => ({
  marginTop: THEME.spacing.lg,
  fontSize: THEME.fontSize.base,
  color: getThemeColor.textSecondary(theme),
  textAlign: 'center',
}));

export const MetadataItem = styled.div<StyledComponentProps>(({ theme }) => ({
  marginBottom: THEME.spacing.xs,
}));

